<template>
    <component :is="tagName">
        <transition
            :name="transition"
            :enter-active-class="enterActiveClass"
            :leave-active-class="leaveActiveClass"
            @after-leave="doDestroy"
        >
            <div v-show="!disabled && showPopper" ref="popper" class="popper" :class="rootClass">
                <slot>{{ content }}</slot>
            </div>
        </transition>
        <slot name="target"></slot>
    </component>
</template>

<script>
    import { createPopper } from "@popperjs/core"
    import { maxZIndex } from "@/util/dom"

    export default {
        props: {
            enableFlip: {
                type: Boolean,
                default: false,
            },
            enableHide: {
                type: Boolean,
                default: false,
            },
            enablePreventOverflow: {
                type: Boolean,
                default: false,
            },

            offset: {
                type: Array,
            },
            placement: {
                type: String,
            },
            tagName: {
                type: String,
                default: "div",
            },
            triggers: {
                type: String,
                default: "",
            },
            delayOnMouseOver: {
                type: Number,
                default: 10,
            },
            delayOnMouseOut: {
                type: Number,
                default: 10,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            content: String,
            enterActiveClass: String,
            leaveActiveClass: String,
            // boundariesSelector: String,
            target: [HTMLElement, String],
            forceShow: {
                type: Boolean,
                default: false,
            },
            appendToScrollContainer: {
                type: Boolean,
                default: true,
            },
            transition: {
                type: String,
                default: "",
            },
            stopPropagation: {
                type: Boolean,
                default: false,
            },
            preventDefault: {
                type: Boolean,
                default: false,
            },
            preventHide: {
                type: Function,
            },
            rootClass: {
                type: String,
                default: "",
            },
            scrollContainer: {
                type: [String, HTMLElement],
            },
        },

        data() {
            const $this = this

            return {
                popperShownOnMouseDown: false,
                targetElm: null,
                popperJS: null,
                showPopper: false,
                currentPlacement: "",
                virtualTarget: {
                    enabled: false,
                    contextElement: null,
                    getBoundingClientRect() {
                        return {
                            ...$this.position,
                            left: $this.position.left - $this.scrollX,
                            right: $this.position.right - $this.scrollX,
                            top: $this.position.top - $this.scrollY,
                            bottom: $this.position.bottom - $this.scrollY,
                        }
                    },
                },
                position: {
                    width: 0,
                    height: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                },
                popperOptions: {
                    placement: "bottom",
                    modifiers: [
                        {
                            name: "hide",
                            enabled: this.enableHide,
                        },
                        {
                            name: "flip",
                            enabled: this.enableFlip,
                        },
                        {
                            name: "preventOverflow",
                            enabled: this.enablePreventOverflow,
                            options: {
                                boundary: document.querySelector(".view-container"),
                            },
                        },
                    ],
                },
            }
        },

        watch: {
            target() {
                const showPopper = this.showPopper
                this.destroyPopper()
                this.configureTarget()
                if (showPopper) {
                    this.showPopper = showPopper
                    this.updatePopper()
                }
            },

            scrollContainer() {
                this.destroyScrollContainer()
                this.configureScrollContainer()
            },

            showPopper(value) {
                if (value) {
                    this.popper.parentElement.style.zIndex = maxZIndex() + 1
                    this.$emit("show", this)
                    if (this.popperJS) {
                        this.popperJS.setOptions({
                            scroll: true,
                            resize: true,
                        })
                    }
                    this.updatePopper()
                } else {
                    if (this.popperJS) {
                        this.popperJS.setOptions({
                            scroll: false,
                            resize: false,
                        })
                    }
                    this.$emit("hide", this)
                }
            },

            forceShow: {
                handler(value) {
                    this[value ? "show" : "hide"]()
                },
                immediate: true,
            },

            disabled(value) {
                if (value) {
                    this.showPopper = false
                }
            },
        },

        created() {
            this.appendedToScrollContainer = false

            if (this.offset) {
                this.popperOptions.modifiers.push({
                    name: "offset",
                    options: { offset: this.offset },
                })
            }

            if (this.placement) {
                this.popperOptions.placement = this.placement
            }

            this.listeners = []
        },

        mounted() {
            this.baseScrollX = 0
            this.baseScrollY = 0
            this.scrollX = 0
            this.scrollY = 0
            this.configureScrollContainer()
            this.configureTarget()
            this.popper = this.$slots.default[0].elm
        },

        destroyed() {
            this.destroyScrollContainer()
            this.destroyPopper()
        },

        methods: {
            configureTarget() {
                this.virtualTarget.enabled = false
                this.targetElm = this.target

                if (!this.targetElm && this.$slots?.target?.length) {
                    this.targetElm = this.$slots.target[0].elm
                }

                if (!this.targetElm) {
                    this.virtualTarget.enabled = true
                    this.targetElm = this.virtualTarget
                }

                if (typeof this.targetElm === "string") {
                    this.targetElm = document.querySelector(this.targetElm)
                }

                if (this.virtualTarget.enabled) {
                    this.addPopperEvent(document, "click", this.handleDocumentClick)
                } else {
                    this.addPopperEvent(this.targetElm, "mousedown", this.onMouseDown)

                    this.triggers.split(/\s+/).forEach(trigger => {
                        switch (trigger) {
                            case "clickToOpen":
                                this.addPopperEvent(this.targetElm, "click", this.show)
                                this.addPopperEvent(document, "click", this.handleDocumentClick)
                                break
                            case "click": // Same as clickToToggle, provided for backwards compatibility.
                            case "clickToToggle":
                                this.addPopperEvent(this.targetElm, "click", this.doToggle)
                                this.addPopperEvent(document, "click", this.handleDocumentClick)
                                break
                            case "hover":
                                this.addPopperEvent(this.targetElm, "mouseover", this.onMouseOver)
                                this.addPopperEvent(this.popper.parentElement, "mouseover", this.onMouseOver)
                                this.addPopperEvent(this.targetElm, "mouseout", this.onMouseOut)
                                this.addPopperEvent(this.popper.parentElement, "mouseout", this.onMouseOut)
                                break
                            case "focus":
                                this.addPopperEvent(this.targetElm, "focus", this.onMouseOver)
                                this.addPopperEvent(this.popper.parentElement, "focus", this.onMouseOver)
                                this.addPopperEvent(this.targetElm, "blur", this.onMouseOut)
                                this.addPopperEvent(this.popper.parentElement, "blur", this.onMouseOut)
                                break
                        }
                    })
                }
            },

            configureScrollContainer() {
                this.scrollContainerElm = this.scrollContainer

                if (typeof this.scrollContainerElm === "string") {
                    this.scrollContainerElm = document.querySelector(this.scrollContainerElm)
                }

                if (!this.scrollContainerElm) {
                    this.scrollContainerElm = document.body
                }

                this.scrollContainerElm.addEventListener("scroll", this.onScroll)
                this.virtualTarget.contextElement = this.scrollContainerElm
            },

            destroyScrollContainer() {
                this.scrollContainerElm.removeEventListener("scroll", this.onScroll)

                if (this.appendedToScrollContainer) {
                    this.appendedToScrollContainer = false
                    this.scrollContainerElm.removeChild(this.popper.parentElement)
                }

                this.scrollContainerElm = null
            },

            onScroll() {
                this.scrollX = this.scrollContainerElm.scrollLeft - this.baseScrollX
                this.scrollY = this.scrollContainerElm.scrollTop - this.baseScrollY
                this.updatePopper()
            },

            doToggle(event) {
                if (this.stopPropagation) {
                    event.stopPropagation()
                }

                if (this.preventDefault) {
                    event.preventDefault()
                }

                if (this.showPopper !== this.popperShownOnMouseDown) {
                    return
                }

                if (!this.forceShow) {
                    this.showPopper = !this.showPopper
                }
            },

            show() {
                this.showPopper = true
            },

            eventHide(event) {
                if (event && this.preventHide && this.preventHide(event)) return
                this.hide()
            },

            hide() {
                this.showPopper = false
            },

            addPopperEvent(element, event, handler) {
                if (element && event && handler) {
                    document.addEventListener
                        ? element.addEventListener(event, handler, false)
                        : element.attachEvent("on" + event, handler)
                    this.listeners.push({ element, event, handler })
                }
            },

            removePopperEvent(element, event, handler) {
                if (element && event) {
                    document.removeEventListener
                        ? element.removeEventListener(event, handler, false)
                        : element.detachEvent("on" + event, handler)
                }
            },

            doDestroy() {
                if (this.showPopper) {
                    return
                }

                if (this.popperJS) {
                    this.popperJS.destroy()
                    this.popperJS = null
                }

                if (this.appendedToScrollContainer) {
                    this.appendedToScrollContainer = false
                    this.scrollContainerElm.removeChild(this.popper.parentElement)
                }
            },

            createPopper() {
                this.$nextTick(() => {
                    this.addPopperEvent(window, "keydown", this.onKeyDown)

                    if (this.appendToScrollContainer && !this.appendedToScrollContainer) {
                        this.appendedToScrollContainer = true
                        this.scrollContainerElm.appendChild(this.popper.parentElement)
                    }

                    if (this.popperJS && this.popperJS.destroy) {
                        this.popperJS.destroy()
                    }

                    this.popperOptions.onFirstUpdate = () => {
                        this.$emit("created", this)
                        this.$nextTick(this.updatePopper)
                    }

                    this.popperJS = createPopper(this.targetElm, this.popper.parentElement, this.popperOptions)
                })
            },

            destroyPopper() {
                this.listeners.forEach(listener => {
                    this.removePopperEvent(listener.element, this.event, this.handler)
                })

                this.showPopper = false
                this.doDestroy()
            },

            moveTo(x, y) {
                this.$nextTick(() => {
                    if (!this.virtualTarget.enabled) {
                        console.log("Only virtual targets are allowed to be moved")
                        return
                    }
                    this.baseScrollX = this.scrollContainerElm.scrollLeft
                    this.baseScrollY = this.scrollContainerElm.scrollTop
                    this.scrollX = 0
                    this.scrollY = 0
                    this.position.left = x
                    this.position.right = x
                    this.position.top = y
                    this.position.bottom = y
                    this.updatePopper()
                })
            },

            updatePopper() {
                this.popperJS ? this.popperJS.update() : this.createPopper()
            },

            onKeyDown(event) {
                if (event.key !== "Escape") return

                if (this.showPopper) {
                    this.destroyPopper()
                }
            },

            onMouseDown() {
                this.popperShownOnMouseDown = this.showPopper
            },

            onMouseOver() {
                clearTimeout(this._timer)
                this._timer = setTimeout(() => {
                    this.showPopper = true
                }, this.delayOnMouseOver)
            },

            onMouseOut(event) {
                clearTimeout(this._timer)
                this._timer = setTimeout(() => {
                    this.eventHide(event)
                }, this.delayOnMouseOut)
            },

            handleDocumentClick(event) {
                if (
                    !this.$el ||
                    !this.targetElm ||
                    this.elementContains(this.$el, event.target) ||
                    this.elementContains(this.targetElm, event.target) ||
                    !this.popper ||
                    this.elementContains(this.popper.parentElement, event.target)
                ) {
                    return
                }

                this.$emit("documentClick", this)

                if (this.forceShow) {
                    return
                }

                this.eventHide(event)
            },

            elementContains(elm, otherElm) {
                if (typeof elm.contains === "function") {
                    return elm.contains(otherElm)
                }

                return false
            },
        },
    }
</script>

<style lang="scss" scoped>
    .popper[data-popper-reference-hidden] {
        visibility: hidden;
        pointer-events: none;
    }
</style>
